import React, { useState } from 'react';
import { Card, Descriptions, Button, Spin, message, Tag } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { useLazyQuery, gql } from '@apollo/client';
import { formatTimeWithTimezone } from '../../utils/timezoneFormatter';

// 根据账期实例ID查询账期详情
const GET_BILLING_PERIOD_INSTANCE = gql`
  query GetBillingPeriodInstanceById($instanceId: Long!) {
    getBillingPeriodInstanceById(instanceId: $instanceId) {
      id
      ruleId
      settlementClientId
      billType
      version
      periodStart
      periodEnd
      closeTime
      issueTime
      summaryTime
      timeZone
      carryOverFlag
      exclusionLogic
      status
      isIssued
      createdAt
      updatedAt
    }
  }
`;

interface BillingPeriodRuleCardProps {
  billingPeriodInstanceId?: string | number;
}

// 周期类型映射
const getCycleTypeLabel = (cycleType: number): string => {
  const cycleTypeMap: Record<number, string> = {
    0: '按日',
    1: '按周',
    2: '按月',
    4: '双周',
  };
  return cycleTypeMap[cycleType] || '未知';
};

// 剔除逻辑映射
const getExclusionLogicLabel = (exclusionLogic: number): string => {
  const exclusionLogicMap: Record<number, string> = {
    0: '不剔除',
    1: '全托剔除',
    2: '半托剔除',
  };
  return exclusionLogicMap[exclusionLogic] || '未知';
};

/**
 * 账期实例展示卡片
 */
const BillingPeriodRuleCard: React.FC<BillingPeriodRuleCardProps> = ({
  billingPeriodInstanceId
}) => {
  const [expanded, setExpanded] = useState(false);

  // 使用懒加载查询账期实例
  const [loadInstance, { data, loading, error }] = useLazyQuery(GET_BILLING_PERIOD_INSTANCE, {
    fetchPolicy: 'network-only',
  });

  const instanceData = data?.getBillingPeriodInstanceById;

  const handleToggle = async () => {
    if (!expanded && !instanceData && billingPeriodInstanceId) {
      // 首次展开时查询数据
      try {
        await loadInstance({
          variables: {
            instanceId: billingPeriodInstanceId
          }
        });
      } catch (error: any) {
        message.error('查询账期信息失败: ' + error.message);
        return;
      }
    }
    setExpanded(!expanded);
  };

  return (
    <Card 
      title="账期信息"
      className="mb-4"
      extra={
        <Button 
          type="link" 
          onClick={handleToggle}
          icon={expanded ? <UpOutlined /> : <DownOutlined />}
        >
          {expanded ? '收起' : '展开查看'}
        </Button>
      }
    >
      {!expanded && (
        <div className="text-gray-500">点击"展开查看"查询此账单关联的账期详情</div>
      )}
      
      {expanded && (
        <>
          {loading ? (
            <div className="text-center py-4">
              <Spin tip="加载账期信息..." />
            </div>
          ) : error ? (
            <div className="text-center py-4 text-red-500">
              加载失败: {error.message}
            </div>
          ) : !instanceData ? (
            <div className="text-center py-4 text-gray-500">
              未找到账期实例信息
            </div>
          ) : (
            <Descriptions column={2} bordered>
              <Descriptions.Item label="账期实例ID">
                {instanceData.id}
              </Descriptions.Item>
              <Descriptions.Item label="规则ID">
                {instanceData.ruleId}
              </Descriptions.Item>
              
              <Descriptions.Item label="版本">
                <Tag>v{instanceData.version}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="时区">
                {instanceData.timeZone}
              </Descriptions.Item>
              
              <Descriptions.Item label="账期开始">
                {formatTimeWithTimezone(instanceData.periodStart, instanceData.timeZone)}
              </Descriptions.Item>
              <Descriptions.Item label="账期结束">
                {formatTimeWithTimezone(instanceData.periodEnd, instanceData.timeZone)}
              </Descriptions.Item>
              
              <Descriptions.Item label="关账时间">
                {formatTimeWithTimezone(instanceData.closeTime, instanceData.timeZone)}
              </Descriptions.Item>
              <Descriptions.Item label="汇总时间">
                {formatTimeWithTimezone(instanceData.summaryTime, instanceData.timeZone)}
              </Descriptions.Item>
              
              <Descriptions.Item label="出账时间">
                {formatTimeWithTimezone(instanceData.issueTime, instanceData.timeZone)}
              </Descriptions.Item>
              <Descriptions.Item label="是否已出账">
                <Tag color={instanceData.isIssued === 1 ? 'green' : 'default'}>
                  {instanceData.isIssued === 1 ? '已出账' : '未出账'}
                </Tag>
              </Descriptions.Item>
              
              <Descriptions.Item label="滚入下期">
                <Tag color={instanceData.carryOverFlag === 1 ? 'green' : 'default'}>
                  {instanceData.carryOverFlag === 1 ? '是' : '否'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="剔除逻辑">
                <Tag color={instanceData.exclusionLogic === 0 ? 'default' : 'orange'}>
                  {getExclusionLogicLabel(instanceData.exclusionLogic)}
                </Tag>
              </Descriptions.Item>
              
              <Descriptions.Item label="创建时间" span={2}>
                {formatTimeWithTimezone(instanceData.createdAt, instanceData.timeZone)} ({instanceData.timeZone})
              </Descriptions.Item>
            </Descriptions>
          )}
        </>
      )}
    </Card>
  );
};

export default BillingPeriodRuleCard;

